import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Truck, RefreshCw, Wrench, DollarSign, Shield, TrendingUp, Clock, Zap } from 'lucide-react';

const solutions = [
  {
    id: 'revolving',
    icon: <RefreshCw className="h-6 w-6" />,
    title: 'Revolving Credit Lines',
    subtitle: 'Flexible, Reusable Capital',
    color: 'teal',
    description: 'Access working capital on demand with revolving credit facilities designed specifically for transportation businesses. Draw funds when you need them and repay as cash flow allows.',
    highlights: [
      { value: 'Up to $500K', label: 'Credit Limit' },
      { value: 'Same Day', label: 'Draw Speed' },
      { value: 'Revolving', label: 'Structure' },
      { value: 'Flexible', label: 'Repayment' },
    ],
    benefits: [
      'Draw only what you need, pay interest only on what you use',
      'Funds replenish automatically as you repay',
      'Cover fuel costs, payroll, insurance, and unexpected expenses',
      'No need to reapply each time you need capital',
      'Designed for the cyclical nature of transportation cash flow',
    ],
    useCases: ['Fuel purchases during high volume periods', 'Payroll between settlement cycles', 'Insurance premium payments', 'Seasonal demand fluctuations'],
  },
  {
    id: 'equipment',
    icon: <Wrench className="h-6 w-6" />,
    title: 'Equipment Financing',
    subtitle: 'Trucks, Trailers, and Fleet Assets',
    color: 'amber',
    description: 'Finance new and used trucks, trailers, refrigeration units, and specialized equipment with competitive rates and terms structured for the transportation industry.',
    highlights: [
      { value: 'Up to $2M', label: 'Per Unit' },
      { value: '2 to 7 Years', label: 'Terms' },
      { value: 'New + Used', label: 'Equipment' },
      { value: 'Quick', label: 'Approval' },
    ],
    benefits: [
      'Finance new and used trucks, trailers, and specialized vehicles',
      'Equipment serves as collateral, reducing documentation requirements',
      'Fixed monthly payments for predictable budgeting',
      'Section 179 tax deduction may apply to qualifying purchases',
      'Flexible down payment options from 0% to 20%',
    ],
    useCases: ['Class 8 trucks and tractor trailers', 'Refrigerated trailers and reefer units', 'Flatbeds, tankers, and specialty trailers', 'GPS, telematics, and fleet technology'],
  },
  {
    id: 'cashout',
    icon: <DollarSign className="h-6 w-6" />,
    title: 'Cash Out Financing',
    subtitle: 'Unlock Fleet Equity',
    color: 'blue',
    description: 'Leverage the equity in your existing fleet to access capital for growth, debt consolidation, or operational needs. Refinance current equipment and extract cash for your business.',
    highlights: [
      { value: 'Up to 80%', label: 'LTV Ratio' },
      { value: 'Fleet Equity', label: 'Collateral' },
      { value: '3 to 5 Years', label: 'Terms' },
      { value: '48 Hours', label: 'Funding Speed' },
    ],
    benefits: [
      'Extract cash from equipment you already own and operate',
      'Refinance high rate loans into lower cost, longer term debt',
      'Consolidate multiple payments into a single monthly obligation',
      'Use funds for any business purpose including expansion or working capital',
      'Keep operating your equipment while accessing its equity',
    ],
    useCases: ['Consolidate MCA or high interest obligations', 'Fund fleet expansion or new route development', 'Cover operating expenses during seasonal slowdowns', 'Invest in technology, hiring, or facility improvements'],
  },
];

const colorMap = {
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', check: 'text-teal-400', btn: 'bg-teal-600 hover:bg-teal-500', gradient: 'from-teal-950' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', check: 'text-amber-400', btn: 'bg-amber-600 hover:bg-amber-500', gradient: 'from-amber-950' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', check: 'text-blue-400', btn: 'bg-blue-600 hover:bg-blue-500', gradient: 'from-blue-950' },
};

const TransportationFinance = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/90 to-slate-900/80" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
                <Truck className="h-4 w-4 text-orange-300" />
                <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Transportation Finance</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Capital That Keeps<br />
                <span className="text-orange-300">Fleets Moving</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-md">
                Revolving credit lines, equipment financing, and cash out solutions built specifically for trucking companies and transportation businesses. Cashflow Engineering for the road.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/apply"
                  data-testid="transport-hero-apply"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all"
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all"
                >
                  Talk to Us
                </Link>
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-2 gap-3">
              {[
                { val: '3', label: 'Financing Solutions' },
                { val: '$2M+', label: 'Per Unit Equipment' },
                { val: '48hr', label: 'Funding Speed' },
                { val: 'All', label: 'Fleet Sizes Welcome' },
              ].map((s) => (
                <div key={s.label} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-xl p-5 text-center">
                  <div className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{s.val}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Three Solutions */}
      {solutions.map((sol, idx) => {
        const colors = colorMap[sol.color];
        return (
          <section key={sol.id} className={`py-20 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} border-b border-slate-100`}>
            <div className="max-w-7xl mx-auto px-6 md:px-8">
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                    <span className={colors.text}>{sol.icon}</span>
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{sol.title}</h2>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400 mb-4">{sol.subtitle}</p>
                <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">{sol.description}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
                {sol.highlights.map((h) => (
                  <div key={h.label} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                    <div className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{h.value}</div>
                    <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{h.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7 bg-slate-900 text-white rounded-2xl p-8">
                  <h3 className="text-xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Key Benefits</h3>
                  {sol.benefits.map((b) => (
                    <div key={b} className="flex items-start gap-3 mb-4 last:mb-0">
                      <CheckCircle className={`h-5 w-5 ${colors.check} flex-shrink-0 mt-0.5`} />
                      <span className="text-sm text-slate-300 leading-relaxed">{b}</span>
                    </div>
                  ))}
                </div>

                <div className="lg:col-span-5 flex flex-col gap-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Common Use Cases</h3>
                    {sol.useCases.map((uc) => (
                      <div key={uc} className="flex items-center gap-2 mb-3 last:mb-0">
                        <Zap className="h-4 w-4 text-[#115E59] flex-shrink-0" />
                        <span className="text-sm text-slate-600">{uc}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/apply"
                    data-testid={`apply-${sol.id}`}
                    className={`${colors.btn} text-white rounded-2xl p-8 flex items-center justify-between group transition-colors`}
                  >
                    <div>
                      <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Apply for {sol.title}</h3>
                      <p className="text-sm text-white/70">Get started in minutes</p>
                    </div>
                    <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Why Quidity */}
      <section className="py-24 bg-slate-900 text-white relative noise-overlay overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-300 mb-3 block">Why Quidity</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Built for Transportation</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Clock className="h-5 w-5" />, title: 'Fast Funding', desc: 'Decisions in hours, not weeks. Capital when your fleet needs it.' },
              { icon: <Shield className="h-5 w-5" />, title: 'Industry Expertise', desc: 'We understand trucking cycles, settlements, and fleet economics.' },
              { icon: <TrendingUp className="h-5 w-5" />, title: 'Growth Focused', desc: 'Solutions designed to help you add trucks, routes, and revenue.' },
              { icon: <RefreshCw className="h-5 w-5" />, title: 'Flexible Structures', desc: 'Repayment terms that align with how transportation businesses earn.' },
            ].map((item) => (
              <div key={item.title} className="bg-white/[0.05] border border-white/10 rounded-2xl p-7">
                <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center mb-4">
                  <span className="text-orange-300">{item.icon}</span>
                </div>
                <h3 className="text-base font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Finance<br />Your Fleet?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            Whether you need revolving credit, equipment financing, or cash out capital, Quidity has the solution for your transportation business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/apply"
              data-testid="transport-final-apply"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-orange-400 transition-colors"
            >
              Apply for Financing <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransportationFinance;
